import {
    MedicalCaseListResponse,
    MedicalCaseVersionListResponse,
    MedicalCaseVersionData,
    MedicalCaseData,
    MedicalCaseCreateParams,
    MedicalCaseUpdateParams,
    MedicalCaseQuery,
    MedicalCaseReferralAddingParams,
    MedicalCaseDiagnosisUpdatingForm,
    MedicalCaseDiagnosisAddingForm,
    MedicalCaseDrugAddingParams,
    MedicalCaseHistoryQuery,
    MedicalCaseAddReferral,
    MedicalCaseIdsWithEntryQuery,
    EpisodeOptimalNameParams,
    EpisodeOptimalNameResponse,
    MedicalCaseCloseDataUpdatingForm,
} from 'chaika-frontend-types/frontend-cms';
import { MedicalCaseStatus } from 'chaika-enums';

import { axios } from '@lib/axios';
import { DateHelper } from '@utils/DateHelper';

const BASE_URL = '/api/medical-case';

export class MedicalCaseApi {
    public static async find(params?: MedicalCaseQuery): Promise<MedicalCaseData[]> {
        const res = await axios.get<MedicalCaseListResponse>(BASE_URL, { params });

        return res.data.medicalCases;
    }

    public static async getEhrIds(params?: MedicalCaseQuery): Promise<string[]> {
        const res = await axios.get(`${BASE_URL}/ehr`, { params });

        return res.data.ehrIds;
    }

    public static async findWithCount(params?: MedicalCaseQuery): Promise<MedicalCaseListResponse> {
        const res = await axios.get<MedicalCaseListResponse>(BASE_URL, { params });

        return res.data;
    }

    public static async getMedicalCaseHistory(params: MedicalCaseHistoryQuery): Promise<MedicalCaseVersionData[]> {
        const res = await axios.get<MedicalCaseVersionListResponse>(`${BASE_URL}/history`, { params });

        return res.data.medicalCaseVersions;
    }

    public static async getMedicalCaseHistoryForEntry(entryId: string): Promise<MedicalCaseVersionData[]> {
        const res = await axios.get<MedicalCaseVersionListResponse>(`${BASE_URL}/history/${entryId}`, {
            skipForbiddenPopup: true,
        });

        return res.data.medicalCaseVersions;
    }

    public static async getById(id: string): Promise<MedicalCaseData> {
        const res = await axios.get<any>(`${BASE_URL}/${id}`, { skipForbiddenPopup: true });

        return res.data.medicalCase;
    }

    public static async getByIds(ids: string[]): Promise<MedicalCaseData[]> {
        const res = await axios.get<any>(`${BASE_URL}`, { params: { id: ids.join(',') } });

        return res.data.medicalCases;
    }

    public static async create(medicalCase: MedicalCaseCreateParams): Promise<void> {
        await axios.post(`${BASE_URL}`, { medicalCase });
    }

    public static async update(id: string, medicalCase: MedicalCaseUpdateParams, entryId?: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}`, { entryId, medicalCase });
    }

    public static async updateStatus(id: string, status: MedicalCaseStatus, entryId?: string): Promise<void> {
        await axios.put(`${BASE_URL}/${id}/status`, { entryId, status });
    }

    public static async updateShareLink(id: string, welcomeMessage: string): Promise<string> {
        await axios.put(`${BASE_URL}/${id}/share-link`, { medicalCase: { welcomeMessage } });
        const { shareLink = '' } = await this.getById(id);

        return shareLink;
    }

    public static async remove(id: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${id}`);
    }

    public static async addEntry(medicalCaseId: string, entryId: string): Promise<void> {
        await axios.post(`${BASE_URL}/${medicalCaseId}/entry`, { entryId });
    }

    public static async removeEntry(medicalCaseId: string, entryId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${medicalCaseId}/entry/${entryId}`);
    }

    public static async addReferral(medicalCaseId: string, params: MedicalCaseReferralAddingParams): Promise<void> {
        await axios.post(`${BASE_URL}/${medicalCaseId}/referral`, params);
    }

    public static async removeReferral(medicalCaseId: string, referralId: string, entryId?: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${medicalCaseId}/referral/${referralId}`, { params: { entryId } });
    }

    public static async addReferralToMedicalCases(referralId: string, params: MedicalCaseAddReferral): Promise<void> {
        await axios.post(`${BASE_URL}/referral/${referralId}`, params);
    }

    public static async removeReferralFromMedicalCases(
        referralId: string,
        params: MedicalCaseIdsWithEntryQuery,
    ): Promise<void> {
        // Backend throws if the list is empty
        if (params.medicalCaseIds.length) {
            await axios.delete(`${BASE_URL}/referral/${referralId}`, {
                params: {
                    ...params,
                    medicalCaseIds: params.medicalCaseIds.join(','),
                },
            });
        }
    }

    public static async addDiagnosis(medicalCaseId: string, params: MedicalCaseDiagnosisAddingForm): Promise<void> {
        await axios.post(`${BASE_URL}/${medicalCaseId}/diagnosis`, params);
    }

    public static async addDrug(medicalCaseId: string, params: MedicalCaseDrugAddingParams): Promise<void> {
        await axios.post(`${BASE_URL}/${medicalCaseId}/drug`, params);
    }

    public static async removeDrug(
        medicalCaseId: string,
        { drugId, entryId }: MedicalCaseDrugAddingParams,
    ): Promise<void> {
        await axios.delete(`${BASE_URL}/${medicalCaseId}/drug/${drugId}`, { params: { entryId } });
    }

    public static async updateDiagnosis(
        medicalCaseId: string,
        diagnosisId: string,
        params: Omit<MedicalCaseDiagnosisUpdatingForm, 'updatedAt'>,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${medicalCaseId}/diagnosis/${diagnosisId}`, {
            ...params,
            updatedAt: DateHelper.now().toISOString(),
        });
    }

    public static async updateCloseData(
        medicalCaseId: string,
        params: MedicalCaseCloseDataUpdatingForm,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${medicalCaseId}/close-data`, params);
    }

    public static async deleteDiagnosis(medicalCaseId: string, diagnosisId: string, entryId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${medicalCaseId}/diagnosis/${diagnosisId}`, { params: { entryId } });
    }

    public static async getEpisodeOptimalName(params: EpisodeOptimalNameParams): Promise<EpisodeOptimalNameResponse> {
        const response = await axios.post<EpisodeOptimalNameResponse>(`${BASE_URL}/optimal-name`, params);

        return response.data;
    }
}
